/**
 * The permission vocabulary — one list, shared by the Express API and the
 * React role editor.
 *
 * This file is the single place a capability is named. The server's
 * `requirePermission` guards read from it, the sidebar and settings tabs
 * filter against it, and the role editor renders itself from it. Adding a
 * screen means adding an entry here and nowhere else; more importantly, a
 * typo'd permission string cannot silently grant nothing on the server while
 * appearing checked in the UI, because both sides resolve the same constant.
 *
 * ── Why `view` / `manage` and not one flag per page ──────────────────────────
 *
 * "Can see the pipeline" and "can move a deal to Won" are different questions,
 * and an agency hiring its first employees will want the first without the
 * second long before it wants anything more granular. Two levels covers that
 * without turning the role editor into a spreadsheet. `manage` always implies
 * `view` (see `expandPermissions`) so nobody can build the impossible role
 * that edits what it cannot open.
 *
 * ── Why a wildcard exists ────────────────────────────────────────────────────
 *
 * The built-in Admin role holds `*`. Enumerating every permission onto it
 * would mean that the day someone adds a screen, the owner of the agency
 * silently loses access to it until a migration backfills the row. A wildcard
 * is the only grant that stays correct as this file grows.
 */

/** Grants everything, including permissions that do not exist yet. */
export const WILDCARD = '*';

/**
 * Every page, settings section and UI capability, in app order.
 *
 * `nav` entries are top-level screens in the icon rail; `settings` entries are
 * tabs inside the settings screen. The `route` is what the client redirects to
 * when it needs to send someone to the first thing they are allowed to open.
 * `ui` entries only toggle controls; they add no navigation or API guard.
 */
export const PERMISSION_GROUPS = [
  {
    key: 'pipeline',
    label: 'CRM Pipeline',
    description: 'Creators, deals, contracts and the outreach board.',
    kind: 'nav',
    route: '/pipeline',
    manageLabel: 'Add creators, move deals, send outreach, issue contracts',
  },
  {
    key: 'pipeline.activeActions',
    label: 'Active Creators: Manual Tracking & Payments',
    description: 'Show the Actions column in Active Creators: record payments, publish videos and view contracts. UI only; API access is unchanged.',
    kind: 'ui',
    route: '/pipeline',
    manage: false,
  },
  {
    key: 'employeeOnboarding',
    label: 'Employee Onboarding',
    description: 'Provision Gmail, proxy and onboarding access for new employees.',
    kind: 'nav',
    route: '/admin/employee-onboarding',
    manageLabel: 'Create, edit, regenerate and revoke onboarding access',
    adminOnly: true,
  },
  {
    key: 'videos',
    label: 'Video Tracking',
    description: 'Posted deliverables and their view counts.',
    kind: 'nav',
    route: '/videos',
    manageLabel: 'Attach posts, refresh stats, mark deliverables',
  },
  {
    key: 'revenue',
    label: 'Revenue Tracking',
    description: 'Stripe revenue per product, customers and payouts.',
    kind: 'nav',
    route: '/revenue',
    manageLabel: 'Add products, connect Stripe keys, trigger syncs',
    /**
     * The one group that can be narrowed further. A role with `revenue.view`
     * may be limited to specific products — see `role_products`.
     */
    productScoped: true,
  },
  {
    key: 'applications',
    label: 'Job Applications',
    description: 'Applicants and private resumes from the careers page.',
    kind: 'nav',
    route: '/applications',
    manageLabel: 'Accept or reject applications and send interview invitations',
  },
  {
    key: 'inbound',
    label: 'Inbound Brand Requests',
    description: 'The queue the public contact form fills.',
    kind: 'nav',
    route: '/inbound',
    manageLabel: 'Claim, reply to and archive requests',
  },
  {
    /**
     * Support, sitting directly below Creator Accounts.
     *
     * ── Why this is a nav screen and not a settings tab ────────────────────
     *
     * It is a queue with a backlog, which is the definition of something people
     * open every day rather than configure once. It is also the only screen in
     * the CRM where somebody outside the company is waiting on the other end of
     * the action, and burying that two clicks deep is how a two-day reply time
     * becomes a two-week one.
     *
     * The view/manage split works as it does elsewhere: reading the queue is
     * safe to grant widely, and `manage` is what actually sends a message to a
     * creator or closes their ticket.
     */
    key: 'support',
    label: 'Creator Support',
    description: 'Tickets from the creator portal.',
    kind: 'nav',
    route: '/support',
    manageLabel: 'Reply to tickets and triage them',
  },
  {
    key: 'settings.campaigns',
    label: 'Email Campaigns',
    description: 'Outreach sequences and their follow-ups.',
    kind: 'settings',
    route: '/settings/campaigns',
    manageLabel: 'Create and edit campaigns',
  },
  {
    key: 'settings.queue',
    label: 'Email Queue',
    description: 'Mail waiting to send, and why it has not.',
    kind: 'settings',
    route: '/settings/queue',
    manageLabel: 'Retry and cancel queued mail (team-wide sending limits are admin-only)',
  },
  {
    key: 'settings.queue-admin',
    label: 'Email Queue Admin',
    description: 'See every employee’s sending limits and usage, and set individual overrides.',
    kind: 'settings',
    route: '/settings/queue-admin',
    manageLabel: 'Change employee sending limits',
    adminOnly: true,
  },
  {
    key: 'settings.activity',
    label: 'Email Activity',
    description: 'What was sent, opened and replied to.',
    kind: 'settings',
    route: '/settings/activity',
    /** Read-only by nature: an audit log nobody should be able to rewrite. */
    manage: false,
  },
  {
    key: 'settings.compliance',
    label: 'Compliance',
    description: 'Sender identity, unsubscribes and suppression.',
    kind: 'settings',
    route: '/settings/compliance',
    manageLabel: 'Change sender identity and suppression lists',
  },
  {
    key: 'settings.contracts',
    label: 'Contracts',
    description: 'Contract templates and signing defaults.',
    kind: 'settings',
    route: '/settings/contracts',
    manageLabel: 'Edit templates and signing settings',
  },
  {
    key: 'settings.api',
    label: 'API & Agents',
    description: 'API keys for external integrations.',
    kind: 'settings',
    route: '/settings/api',
    manageLabel: 'Issue, rotate and revoke keys',
  },
  {
    key: 'settings.slack',
    label: 'Slack Notifications',
    description: 'Slack channels for published videos and paid creators.',
    kind: 'settings',
    route: '/settings/slack',
    manageLabel: 'Configure Slack notification webhooks',
    adminOnly: true,
  },
  {
    /**
     * The onboarding calendar.
     *
     * ── Why this is a settings tab and not part of Support ─────────────────
     *
     * It shipped inside Creator Support because both were built in the same
     * change and both concern creators. That is a reason for two things to be
     * adjacent, not for them to be the same screen.
     *
     * What actually distinguishes them is how they are used. Support is a queue
     * with a backlog — somebody works it daily, and the cost of burying it is a
     * creator waiting. The calendar is configuration: somebody opens two weeks
     * of availability, and then nobody touches it until it runs low. Sitting a
     * "configure this occasionally" panel behind a tab in a "work this every
     * day" screen made the queue's own tab strip a place to hunt for settings.
     *
     * Its own permission rather than riding on `support`, because the two
     * grants are genuinely different: answering a creator's ticket is a
     * day-to-day rep action, while opening and withdrawing slots edits the
     * team's calendar and decides whether newly signed creators can finish
     * onboarding at all. A rep can now have one without the other.
     */
    key: 'settings.calendar',
    label: 'Onboarding',
    description: 'The video new creators watch, and the calls they can book.',
    kind: 'settings',
    route: '/settings/onboarding',
    manageLabel: 'Set the onboarding video and manage bookable times',
  },
  {
    key: 'settings.team',
    label: 'Team & Roles',
    description: 'Who is in the agency and what each role can reach.',
    kind: 'settings',
    route: '/settings/team',
    manageLabel: 'Create roles, assign them, deactivate people',
  },
  {
    key: 'settings.offboarding',
    label: 'Offboarding',
    description: 'Transfer employee work and revoke CRM access without losing creators.',
    kind: 'settings',
    route: '/settings/offboarding',
    manageLabel: 'Transfer work and offboard employees',
    adminOnly: true,
  },
  {
    key: 'settings.products',
    label: 'Creator Products',
    description: 'Credentials used to provision product access for creators.',
    kind: 'settings',
    route: '/settings/products',
    manageLabel: 'Configure product provisioning credentials',
    adminOnly: true,
  },
  {
    /**
     * The copy of every automatic email, and an ADMIN-ONLY group.
     *
     * ── Why it is not a grantable permission ─────────────────────────────────
     *
     * Editing these templates is not "a screen with edit rights". The messages
     * go out automatically, from a colleague's own mailbox, to creators who are
     * about to sign something — so whoever holds this can change what the
     * agency appears to say, in someone else's name, to everyone, with no
     * further approval. There is no per-send review that would catch a bad
     * edit either: the next contract simply uses the new wording.
     *
     * That is an owner's decision rather than a delegated one, and the honest
     * way to express "owner only" here is to make the permission UNGRANTABLE
     * rather than merely unticked by default. `adminOnly` keeps this group out
     * of `ALL_PERMISSIONS`, which means:
     *
     *   · the role editor never offers it — it renders from the catalog;
     *   · `role.service`'s zod enum refuses it if posted directly;
     *   · `sanitizePermissions` drops it on read, so even a row inserted into
     *     `role_permissions` by hand is inert.
     *
     * The only thing that satisfies `settings.emails.*` is therefore the
     * wildcard itself — by construction rather than by configuration. An admin
     * who genuinely wants to delegate this grants admin, which is at least an
     * honest description of what they are handing over.
     */
    key: 'settings.emails',
    label: 'System Emails',
    description: 'The wording of the emails the system sends on its own.',
    kind: 'settings',
    route: '/settings/emails',
    manageLabel: 'Edit the copy of automatic emails',
    adminOnly: true,
  },
];

/** Groups a role can actually be given. See `ALL_PERMISSIONS`. */
export const GRANTABLE_GROUPS = PERMISSION_GROUPS.filter((group) => !group.adminOnly);

/**
 * `pipeline.view`, `pipeline.manage`, … — every string a role MAY hold.
 *
 * `adminOnly` groups are excluded, and that omission is the entire enforcement
 * mechanism behind them. Everything downstream derives from this list: the
 * role editor's checkboxes, the zod enum that validates a submitted role, and
 * `PERMISSION_SET`, which `sanitizePermissions` uses to drop anything
 * unrecognised on the way OUT of the database.
 *
 * So a permission absent from here cannot be granted, cannot be stored
 * usefully, and cannot be honoured if it somehow is — leaving the wildcard as
 * the only thing that satisfies it.
 */
export const ALL_PERMISSIONS = GRANTABLE_GROUPS.flatMap((group) =>
  group.manage === false ? [`${group.key}.view`] : [`${group.key}.view`, `${group.key}.manage`],
);

const PERMISSION_SET = new Set(ALL_PERMISSIONS);

/** Groups that gate a top-level screen, in rail order. */
export const NAV_GROUPS = PERMISSION_GROUPS.filter((group) => group.kind === 'nav');

/** Groups that gate a settings tab, in tab order. */
export const SETTINGS_GROUPS = PERMISSION_GROUPS.filter((group) => group.kind === 'settings');

/** Is this a permission this build knows about? */
export function isKnownPermission(permission) {
  return permission === WILDCARD || PERMISSION_SET.has(permission);
}

/**
 * Drop anything unrecognised from a list of permissions.
 *
 * Stored rows outlive the code that wrote them: a permission removed from the
 * catalog in a later release is still sitting in `role_permissions`. Filtering
 * on read means the stale row is inert rather than a crash, and the role
 * editor never renders a checkbox for a capability that no longer exists.
 */
export function sanitizePermissions(permissions) {
  const list = Array.isArray(permissions) ? permissions : [];
  return [...new Set(list.filter(isKnownPermission))];
}

/**
 * Apply the implications, so a stored set becomes an effective set.
 *
 * `x.manage` implies `x.view`. The implication is resolved here rather than
 * written into the database, because a rule stored as rows has to be re-run
 * with a migration every time it changes, and because a checkbox pair in the
 * UI that silently ticks its sibling is easier to reason about when the
 * server is the thing enforcing it.
 */
export function expandPermissions(permissions) {
  const effective = new Set();

  for (const permission of sanitizePermissions(permissions)) {
    effective.add(permission);
    if (permission.endsWith('.manage')) {
      effective.add(`${permission.slice(0, -'.manage'.length)}.view`);
    }
  }

  return effective;
}

/**
 * The one check, used everywhere.
 *
 * Takes the *stored* list and does the expansion itself, so no caller can get
 * a different answer by forgetting to expand first. `permissions` may be an
 * array or a Set; `*` short-circuits.
 */
export function hasPermission(permissions, required) {
  if (!required) return true;

  const list = permissions instanceof Set ? [...permissions] : (permissions ?? []);
  if (list.includes(WILDCARD)) return true;

  return expandPermissions(list).has(required);
}

/** True when any of `required` is held. Used for "can open this screen at all". */
export function hasAnyPermission(permissions, required = []) {
  return required.some((permission) => hasPermission(permissions, permission));
}

/**
 * Where to send someone who just signed in, or who followed a link into a
 * screen they cannot open.
 *
 * Walks the nav groups in rail order and returns the first one they can view,
 * then the settings tabs. Returns null when a role grants nothing at all —
 * the client renders a "no access" screen rather than a redirect loop, which
 * is the honest outcome of an empty role and the one that gets it fixed.
 */
export function firstAllowedRoute(permissions) {
  const group = PERMISSION_GROUPS.find(
    (entry) => entry.kind !== 'ui' && hasPermission(permissions, `${entry.key}.view`),
  );
  return group ? group.route : null;
}

/**
 * Metadata for a permission string, e.g. `revenue.view` → the Revenue group.
 * Used to turn a 403 into a sentence that names the screen.
 */
export function permissionGroup(permission) {
  const key = String(permission ?? '').replace(/\.(view|manage)$/, '');
  return PERMISSION_GROUPS.find((group) => group.key === key) ?? null;
}
